import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { promoteDiscovered } from '../api/discoveredActions'
import {
  inventoryMapConfirm,
  inventoryMapDataQuery,
  inventoryMapIgnore,
  inventoryMapPageQuery,
  inventoryMapUpdate,
  inventoryMapUplink,
} from '../api/inventoree'
import { shellQuery } from '../api/shell'
import { Forbidden, isForbidden } from '../ui/Forbidden'
import { SettingsCheck } from '../ui/SettingsParts'
import { Content } from '../ui/Shell'
import { usePageStyles } from '../ui/usePageStyles'
import { brandIdForVendor, canAddToInventory, ipFromNode, typeIdForKind } from './map/addFacts'
import { applySearch, clearHighlight, drawMap, fitMap, forgetPositions, keepViewport, showImpact, type Cy } from './map/draw'
import { officeNamesFrom, groupByOffice } from './map/officeBoxes'
import { panelOpen as isPanelOpen } from './map/panelOpen'
import { attachWheelZoom, zoomKey, zoomStep } from './map/zoom'
import { ZoomBar } from './map/ZoomBar'
import './InventoryMapPage.css'

type Editing = {
  deviceId?: number
  kind?: string
  name?: string
  typeId?: number
  brandId?: number
  officeId?: number
  segmentKey?: string
  uplinkTo?: number | null
  /** Cand se trece in inventar un aparat gasit pe retea (`found-…`). */
  foundId?: number
  /** Adresa pe care harta o stia deja (poarta votata, aparat gasit). */
  ip?: string
  mac?: string
  vendor?: string
}

/** Cutiile de retea - singurele care se pot confirma si sub care se leaga altele. */
const GEAR: Record<string, string> = { switch: 'switch', gateway: 'gateway', ap: 'access point' }

type MapNode = Record<string, unknown>

function text(value: unknown): string {
  return value === null || value === undefined ? '' : String(value)
}

function numberOf(value: unknown): number | undefined {
  return typeof value === 'number' ? value : undefined
}

/**
 * Inventoree::inventory_map - macheta inventory_map/index.php.
 * Scrierile POST sunt inventoryMapUpdate / Confirm / Uplink / Ignore, plus
 * promote pentru un aparat gasit pe retea.
 */
export function InventoryMapPage() {
  const shell = useQuery(shellQuery('inventoree'))
  usePageStyles(['css/inventoree_map.css'], shell.data?.version ?? '')

  const page = useQuery(inventoryMapPageQuery)
  const [office, setOffice] = useState('')
  const [search, setSearch] = useState('')
  const [unregistered, setUnregistered] = useState(() => {
    try {
      return window.localStorage.getItem('imap-unregistered') !== '0'
    } catch {
      return true
    }
  })
  const [at, setAt] = useState('')
  const [editing, setEditing] = useState<Editing | null>(null)
  const [quickError, setQuickError] = useState('')
  const [saving, setSaving] = useState(false)
  const [managed, setManaged] = useState(false)
  const [snmpVersion, setSnmpVersion] = useState('2c')
  const [hasLogin, setHasLogin] = useState(false)
  /** Nodul apasat, desenat in panoul din dreapta. */
  const [selected, setSelected] = useState<MapNode | null>(null)
  /** Are nodul apasat ceva sub el? Se afla la apasare, cat panza e desenata. */
  const [selectedImpact, setSelectedImpact] = useState(false)
  /** Cate dispozitive raman fara drum daca pica nodul apasat; null = nu s-a intrebat. */
  const [impactCount, setImpactCount] = useState<number | null>(null)
  const graph = useQuery(inventoryMapDataQuery(office, at))
  const canvas = useRef<HTMLDivElement>(null)
  const cyRef = useRef<Cy | null>(null)
  /* Aceeasi panza, ca stare: bara de zoom trebuie sa se redeseneze cand vine una noua. */
  const [cyNow, setCyNow] = useState<Cy | null>(null)
  const wrap = useRef<HTMLDivElement>(null)
  const adding = !!editing && !editing.deviceId
  const promoting = !!editing?.foundId

  useEffect(() => {
    try {
      window.localStorage.setItem('imap-unregistered', unregistered ? '1' : '0')
    } catch {
      /* */
    }
  }, [unregistered])

  /** Editarea rapida de langa un nod - clic-dreapta, sau butoanele din panou. */
  function openQuick(node: MapNode) {
    if (node.kind === 'internet' || node.kind === 'network' || node.kind === 'egress') return
    if (node.public === true) return
    setQuickError('')
    setManaged(false)
    setHasLogin(false)
    setSnmpVersion('2c')
    setSelected(null)
    setImpactCount(null)
    const kind = typeof node.kind === 'string' ? node.kind : undefined
    const vendor = typeof node.vendor === 'string' ? node.vendor : ''
    const types = page.data?.deviceTypes ?? []
    const brands = page.data?.brands ?? []
    setEditing({
      deviceId: numberOf(node.deviceId),
      kind,
      name: typeof node.label === 'string' ? node.label : '',
      typeId: numberOf(node.typeId) ?? typeIdForKind(kind, types),
      brandId: numberOf(node.brandId) ?? brandIdForVendor(vendor, brands),
      officeId: numberOf(node.officeId),
      segmentKey: typeof node.segmentKey === 'string' ? node.segmentKey : undefined,
      uplinkTo: numberOf(node.uplinkTo) ?? null,
      foundId: numberOf(node.foundId),
      ip: ipFromNode(node),
      mac: typeof node.mac === 'string' ? node.mac : undefined,
      vendor: vendor || undefined,
    })
  }

  /*
   * Cutiile de sediu: doar pe "All offices", si doar cand ies cel putin doua.
   * Numele vin din lista de sedii a ecranului si din nota grafului.
   */
  const officeNames = useMemo(
    () => officeNamesFrom(page.data?.offices ?? [], (graph.data?.offices as { id: number; name: string }[] | undefined) ?? []),
    [page.data, graph.data],
  )
  const officeGroups = useMemo(() => {
    const data = graph.data
    if (!data || office !== '') return null
    const nodes = ((data.nodes as MapNode[]) ?? []).filter((n) => unregistered || n.evidence !== 'unregistered')
    return groupByOffice(nodes, (data.edges as MapNode[]) ?? [], officeNames)
  }, [graph.data, unregistered, office, officeNames])
  /*
   * Pozitiile mutate cu mana stau sub cheia sediului (`imap.pos.all` pe "All
   * offices"), cu sau fara cutii: harta e acelasi cerc, deci oamenii isi
   * regasesc mutarile de dinainte de cutii.
   */
  const positionsScope = office

  /*
   * Semnatura grafului: refetech-ul de pe focus intoarce deseori acelasi
   * continut cu ALT obiect. Daca efectul ar depinde de `graph.data`, panza
   * s-ar recrea de fiecare data si mutarile abia facute ar parea nesalvate.
   */
  const graphSig = useMemo(() => {
    const data = graph.data
    if (!data) return ''
    const nodes = ((data.nodes as MapNode[]) ?? [])
      .filter((n) => unregistered || n.evidence !== 'unregistered')
      .map((n) => text(n.id))
      .join('\n')
    const edges = ((data.edges as MapNode[]) ?? [])
      .map((e) => text(e.source) + '>' + text(e.target) + ':' + text(e.label))
      .join('\n')
    const boxes = officeGroups ? officeGroups.boxes.map((b) => b.id + '=' + b.label).join('|') : ''
    return nodes + '#' + edges + '#' + office + '#' + boxes
  }, [graph.data, unregistered, office, officeGroups])

  useEffect(() => {
    const box = canvas.current
    const data = graph.data
    if (!box || !data || graphSig === '') return
    setSelected(null)
    setImpactCount(null)
    let gone = false
    void drawMap(
      box,
      {
        nodes: ((data.nodes as MapNode[]) ?? []).filter((n) => unregistered || n.evidence !== 'unregistered'),
        edges: (data.edges as MapNode[]) ?? [],
      },
      {
        onTap: (node) => {
          setImpactCount(null)
          setEditing(null)
          const cy = cyRef.current
          if (cy) clearHighlight(cy)
          const id = text(node.id)
          setSelectedImpact(!!cy && cy.$id(id).length > 0 && cy.$id(id).successors().length > 0)
          setSelected(node)
        },
        onContext: (node) => openQuick(node),
        onBackground: () => {
          setSelected(null)
          setImpactCount(null)
          const cy = cyRef.current
          if (cy) clearHighlight(cy)
        },
        positionsScope,
        officeGroups,
      },
    ).then((cy) => {
      if (gone) {
        cy?.destroy()
        return
      }
      cyRef.current?.destroy()
      cyRef.current = cy
      setCyNow(cy)
    })
    return () => {
      gone = true
      cyRef.current?.destroy()
      cyRef.current = null
      setCyNow(null)
    }
  }, [graphSig])

  /* Rotita: zoom in jurul cursorului, cu pas de om (map/zoom.ts), nu cel al lui Cytoscape. */
  useEffect(() => {
    const outer = wrap.current
    const box = canvas.current
    if (!outer || !box) return
    return attachWheelZoom(outer, box, () => cyRef.current)
  }, [])

  /* + / - / 0 cand harta (sau bara ei) are focus. Campul de cautare e in afara, deci nu se calca. */
  function onMapKey(ev: React.KeyboardEvent<HTMLDivElement>) {
    const cy = cyRef.current
    const what = cy ? zoomKey(ev) : null
    if (!cy || !what) return
    ev.preventDefault()
    if (what === 'fit') fitMap(cy)
    else zoomStep(cy, what === 'in' ? 1 : -1)
  }

  /* Cautarea aprinde ce se potriveste si stinge restul, pe panza deja desenata. */
  useEffect(() => {
    const cy = cyRef.current
    if (cy) applySearch(cy, search)
  }, [search, graph.data, unregistered])

  /*
   * Panoul din dreapta ingusteaza panza. Spunem motorului noua latime, dar
   * lasam zoom-ul (si pan-ul) pe care omul le-a ales cu rotita / drag - un
   * `fit` la fiecare clic pe echipament arunca incadrarea manuala.
   * Formularul „Add to Inventory” sta tot aici (nu plutitor pe panza) - altfel
   * dupa apasare panoul se inchidea si fereastra absoluta, fara top/left, cadea
   * sub panza (taiata de overflow) si parea ca butonul nu face nimic.
   */
  const panelOpen = isPanelOpen(selected, editing)
  useEffect(() => {
    const cy = cyRef.current
    if (!cy) return
    keepViewport(cy)
  }, [panelOpen])

  async function saveQuick() {
    if (!editing) return
    const name = (document.getElementById('imapQuickName') as HTMLInputElement | null)?.value ?? ''
    const type = (document.getElementById('imapQuickType') as HTMLSelectElement | null)?.value ?? ''
    const brand = (document.getElementById('imapQuickBrand') as HTMLSelectElement | null)?.value ?? ''
    const officeId = (document.getElementById('imapQuickOffice') as HTMLSelectElement | null)?.value ?? ''
    setSaving(true)
    setQuickError('')
    try {
      /*
       * Un aparat vazut in retea si netrecut in inventar merge pe drumul lui de
       * promovare - EXISTA deja in tabela de descoperire, cu MAC si adresa. Nu
       * se inventeaza nimic, doar se trece in evidenta.
       */
      if (editing.foundId) {
        const answer = await promoteDiscovered(editing.foundId, {
          device_name: name,
          device_type: type,
          device_brand: brand,
          device_office: officeId,
        })
        if (answer.status !== 'OK') {
          setQuickError(answer.message || 'It could not be added.')
          return
        }
        setEditing(null)
        await graph.refetch()
        return
      }

      const answer = adding
        ? await inventoryMapConfirm({
            kind: editing.kind ?? 'switch',
            name,
            type,
            brand,
            office: officeId,
            ip: (document.getElementById('imapQuickIp') as HTMLInputElement | null)?.value ?? editing.ip ?? '',
            managed: managed ? 1 : 0,
            snmp_version: snmpVersion,
            snmp_port: (document.getElementById('imapQuickPort') as HTMLInputElement | null)?.value ?? '161',
            snmp_community: managed && snmpVersion !== '3' ? ((document.getElementById('imapQuickCommunity') as HTMLInputElement | null)?.value ?? '') : '',
            snmp_user: managed && snmpVersion === '3' ? ((document.getElementById('imapQuickSnmpUser') as HTMLInputElement | null)?.value ?? '') : '',
            snmp_password: managed && snmpVersion === '3' ? ((document.getElementById('imapQuickSnmpPass') as HTMLInputElement | null)?.value ?? '') : '',
            login_user: managed && hasLogin ? ((document.getElementById('imapQuickLoginUser') as HTMLInputElement | null)?.value ?? '') : '',
            login_password: managed && hasLogin ? ((document.getElementById('imapQuickLoginPass') as HTMLInputElement | null)?.value ?? '') : '',
          })
        : await inventoryMapUpdate({
            deviceId: editing.deviceId ?? 0,
            name,
            type,
            brand,
            office: officeId,
          })
      if (answer.status !== 'OK') {
        setQuickError(answer.message || 'It could not be saved.')
        return
      }
      if (answer.credential && answer.credential.status !== 'OK') {
        setQuickError(answer.credential.message || 'The credentials were not saved.')
        return
      }
      const switchId = Number((document.getElementById('imapQuickUplink') as HTMLSelectElement | null)?.value ?? '')
      if (!adding && editing.deviceId && Number.isFinite(switchId)) {
        const link = await inventoryMapUplink(editing.deviceId, switchId)
        if (link.status !== 'OK') {
          setQuickError(link.message || 'It could not be saved.')
          return
        }
      }
      setEditing(null)
      await graph.refetch()
    } catch (error) {
      setQuickError(error instanceof Error ? error.message : 'It could not be saved.')
    } finally {
      setSaving(false)
    }
  }

  async function dropAssumed() {
    if (!editing?.segmentKey) return
    setSaving(true)
    setQuickError('')
    try {
      const answer = await inventoryMapIgnore({ segment_key: editing.segmentKey })
      if (answer.status !== 'OK') {
        setQuickError(answer.message || 'It could not be saved.')
        return
      }
      setEditing(null)
      await graph.refetch()
    } catch (error) {
      setQuickError(error instanceof Error ? error.message : 'It could not be saved.')
    } finally {
      setSaving(false)
    }
  }

  async function restoreHidden() {
    setSaving(true)
    try {
      await inventoryMapIgnore({ all: 1 })
      await graph.refetch()
    } finally {
      setSaving(false)
    }
  }

  /** "Daca pica asta?" - evidentiaza ce atarna dedesubt si scrie cate dispozitive raman fara drum. */
  function askImpact(node: MapNode) {
    const cy = cyRef.current
    if (!cy) return
    setImpactCount(showImpact(cy, text(node.id)))
  }

  /** Trimite "aparatul asta e legat in comutatorul ala", sau il scoate de acolo (0). */
  async function setUplink(deviceId: number, switchId: number) {
    setSaving(true)
    try {
      await inventoryMapUplink(deviceId, switchId)
      await graph.refetch()
    } finally {
      setSaving(false)
    }
  }

  if (isForbidden(page.error) || isForbidden(graph.error)) return <Forbidden />

  const offices = page.data?.offices ?? []
  const types = page.data?.deviceTypes ?? []
  const brands = page.data?.brands ?? []
  const counts = (graph.data?.counts ?? {}) as { devices?: number; unregistered?: number; ignored?: number }
  const history = (graph.data?.history ?? {}) as { from?: string; to?: string; at?: string | null }
  const uplinksExist = graph.data?.uplinks === true
  const allNodes = ((graph.data?.nodes as MapNode[]) ?? []).filter((n) => unregistered || n.evidence !== 'unregistered')
  const empty = !graph.isFetching && allNodes.length === 0
  const searchLc = search.trim().toLowerCase()

  /** Ce se stie despre un dispozitiv, in perechi nume-valoare (ca `facts()` din inventory_map.js). */
  function facts(node: MapNode): [string, string][] {
    const link = text(node.link)
    return (
      [
        ['Type', text(node.type)],
        ['Network', text(node.netLabel)],
        ['Office', text(node.officeName)],
        ['MAC', text(node.mac)],
        ['Goes out as', text(node.egress)],
        ['Devices behind it', node.behind ? text(node.behind) : ''],
        ['Networks behind it', node.netsBehind ? text(node.netsBehind) : ''],
        ['Connection', link === 'wireless' ? 'wireless' : link === 'wired' ? 'wired' : ''],
        ['Adapter', text(node.adapter)],
        ['Runs on', text(node.hostName)],
        ['First seen', text(node.firstSeen)],
        ['In the inventory since', text(node.addedOn)],
        ['Last seen', text(node.lastSeen)],
        ['Agent', node.deviceId ? (node.agent ? 'yes' : 'no') : ''],
      ] as [string, string][]
    ).filter((row) => row[1] !== '')
  }

  /** Aparatele legate in acest comutator, dupa ce a spus un om (`connectedSection`). */
  function attachedTo(deviceId: number): MapNode[] {
    return allNodes.filter((n) => numberOf(n.uplinkTo) === deviceId)
  }

  const deviceHref = import.meta.env.BASE_URL + 'inventoree/device/'

  function detailBody(node: MapNode) {
    const impactNote =
      impactCount === null ? null : (
        <div className="imap-impact-note">
          {impactCount === 0 ? 'Nothing else depends on it.' : `${impactCount} ${impactCount === 1 ? 'device' : 'devices'} lose their way out if this fails.`}
        </div>
      )

    const impactButton = selectedImpact ? (
      <button className="btn btn-xs btn-danger imap-open imap-impact" type="button" onClick={() => askImpact(node)}>
        <i className="fas fa-bolt"></i> If this fails
      </button>
    ) : null

    const head = (
      <div>
        <strong>{text(node.label)}</strong>
        {node.sublabel ? (
          <>
            {' '}
            <span className="imap-muted">{text(node.sublabel)}</span>
          </>
        ) : null}
      </div>
    )

    /* Un dispozitiv adevarat: fisa scurta, legatura spre fisa intreaga, impact si legaturile din comutator. */
    if (node.deviceId) {
      const deviceId = numberOf(node.deviceId) ?? 0
      const isGear = typeof node.kind === 'string' && !!GEAR[node.kind]
      return (
        <>
          {head}
          <dl className="imap-facts">
            {facts(node).map(([label, value]) => (
              <div key={label} style={{ display: 'contents' }}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
          <a className="btn btn-xs btn-primary imap-open" href={deviceHref + deviceId} target="_blank" rel="noopener">
            Open device <i className="fas fa-external-link-alt"></i>
          </a>
          {impactButton}
          {impactNote}
          {uplinksExist && isGear ? (
            <div className="imap-conn">
              <h6>Connected devices</h6>
              {attachedTo(deviceId).length === 0 ? <p className="imap-muted">Nobody has said yet what hangs off it.</p> : null}
              {attachedTo(deviceId).map((child) => (
                <div className="imap-conn-row" key={text(child.deviceId)}>
                  <span>{text(child.label) + (child.uplinkPort ? ' · ' + text(child.uplinkPort) : '')}</span>
                  <button
                    type="button"
                    className="imap-conn-off"
                    title="Not connected here"
                    disabled={saving}
                    onClick={() => void setUplink(numberOf(child.deviceId) ?? 0, 0)}
                  >
                    &times;
                  </button>
                </div>
              ))}
              <select
                className="form-control input-sm imap-conn-pick"
                value=""
                disabled={saving}
                onChange={(e) => {
                  if (e.target.value) void setUplink(Number(e.target.value), deviceId)
                }}
              >
                <option value="">add a device…</option>
                {allNodes
                  .filter((n) => n.deviceId && numberOf(n.deviceId) !== deviceId && !n.uplinkTo && !(typeof n.kind === 'string' && GEAR[n.kind]))
                  .map((n) => (
                    <option key={text(n.deviceId)} value={text(n.deviceId)}>
                      {text(n.label)}
                    </option>
                  ))}
              </select>
            </div>
          ) : null}
        </>
      )
    }

    /* Vazut in retea, dar nu in inventar: ce stim si butonul care il trece in evidenta. */
    if (node.foundId) {
      const knownIp = ipFromNode(node)
      return (
        <>
          {head}
          <dl className="imap-facts">
            {knownIp ? (
              <>
                <dt>IP</dt>
                <dd>{knownIp}</dd>
              </>
            ) : null}
            {node.vendor ? (
              <>
                <dt>Made by</dt>
                <dd>{text(node.vendor)}</dd>
              </>
            ) : null}
            {node.mac ? (
              <>
                <dt>MAC</dt>
                <dd>{text(node.mac)}</dd>
              </>
            ) : null}
            {node.firstSeen ? (
              <>
                <dt>First seen</dt>
                <dd>{text(node.firstSeen)}</dd>
              </>
            ) : null}
            {node.lastSeen ? (
              <>
                <dt>Last seen</dt>
                <dd>{text(node.lastSeen)}</dd>
              </>
            ) : null}
          </dl>
          <div className="imap-muted">Seen on the network, but not in the inventory.</div>
          <button className="btn btn-xs btn-primary imap-open" type="button" onClick={() => openQuick(node)}>
            <i className="fas fa-plus"></i> Add to Inventory
          </button>
          {impactButton}
          {impactNote}
        </>
      )
    }

    /*
     * Nu e in inventar, dar harta stie ceva despre el (poarta votata, switch
     * ghicit, AP): Add to Inventory cu ce s-a aflat pana acum.
     */
    if (canAddToInventory(node, GEAR)) {
      const knownIp = ipFromNode(node)
      return (
        <>
          {head}
          <dl className="imap-facts">
            {knownIp ? (
              <>
                <dt>IP</dt>
                <dd>{knownIp}</dd>
              </>
            ) : null}
            {node.vendor ? (
              <>
                <dt>Made by</dt>
                <dd>{text(node.vendor)}</dd>
              </>
            ) : null}
            {node.mac ? (
              <>
                <dt>MAC</dt>
                <dd>{text(node.mac)}</dd>
              </>
            ) : null}
            {node.netLabel ? (
              <>
                <dt>Network</dt>
                <dd>{text(node.netLabel)}</dd>
              </>
            ) : null}
          </dl>
          <div className="imap-muted">
            {node.evidence === 'assumed'
              ? 'Inferred from the addresses - not seen on the network.'
              : 'Seen on the network, but not in the inventory.'}
          </div>
          <button
            className={`btn btn-xs imap-open ${node.evidence === 'assumed' ? 'btn-warning' : 'btn-primary'}`}
            type="button"
            onClick={() => openQuick(node)}
          >
            <i className="fas fa-plus"></i> Add to Inventory
          </button>
          {impactButton}
          {impactNote}
        </>
      )
    }

    return (
      <>
        {head}
        {impactButton}
        {impactNote}
      </>
    )
  }

  return (
    <Content>
      <div className="imap-bar">
        <div className="imap-field">
          <label className="sr-only" htmlFor="imapOffice">
            Office
          </label>
          <select className="form-control input-sm" id="imapOffice" value={office} onChange={(e) => setOffice(e.target.value)}>
            <option value="">All offices</option>
            {offices.map((o) => (
              <option key={o.ID} value={String(o.ID)}>
                {o.Nume}
              </option>
            ))}
          </select>
        </div>
        <div className="imap-field">
          <label className="sr-only" htmlFor="imapSearch">
            Find
          </label>
          <input type="text" className="form-control input-sm" id="imapSearch" placeholder="name, IP or MAC" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="imap-field">
          <span className="imap-check">
            <SettingsCheck
              id="imapUnregistered"
              checked={unregistered}
              onChange={setUnregistered}
              label={
                <>
                  Include Auto-Discovered Items <span id="imapUnregisteredCount">{counts.unregistered ? `(${counts.unregistered})` : ''}</span>
                </>
              }
            />
          </span>
        </div>
        <span className="imap-spacer"></span>
        <span className="imap-count" id="imapCount">
          {graph.isFetching ? '' : `${counts.devices ?? allNodes.length} devices`}
        </span>
        <button
          className="btn btn-link btn-sm imap-hidden-note"
          id="imapHidden"
          style={{ display: counts.ignored ? undefined : 'none' }}
          type="button"
          disabled={saving}
          onClick={() => void restoreHidden()}
        >
          {counts.ignored ? `${counts.ignored} hidden` : ''}
        </button>
        <button className="btn btn-default btn-sm" id="imapRefresh" title="Redraw" type="button" onClick={() => void graph.refetch()}>
          <i className="fas fa-sync-alt"></i>
        </button>
      </div>

      <div className="imap-time" id="imapTime" style={{ display: history.from ? undefined : 'none' }}>
        <div className="imap-time-lead">
          <span className="imap-time-what">How the system looked</span>
          <span className="imap-time-line">
            <span className="imap-time-day" id="imapTimeDay">
              {at || 'today'}
            </span>
            <span className="imap-time-note" id="imapTimeNote" style={{ display: at ? undefined : 'none' }}>
              &middot; addresses as known today
            </span>
          </span>
        </div>
        <div className="imap-time-track">
          <input
            type="range"
            id="imapTimeSlider"
            className="imap-time-slider"
            min={0}
            max={1}
            value={at ? 0 : 1}
            step={1}
            onChange={(e) => setAt(Number(e.target.value) === 1 ? '' : (history.from ?? ''))}
          />
          <div className="imap-time-scale" id="imapTimeScale">
            {history.from} — {history.to}
          </div>
        </div>
        <button type="button" className="btn btn-xs btn-default imap-time-now" id="imapTimeNow" title="Back to today" onClick={() => setAt('')}>
          Now
        </button>
      </div>

      <div className="imap-legend">
        <span>
          <i className="fas fa-globe" style={{ color: '#7d5ba6' }}></i> the public address each network goes out through
        </span>
        <span>
          <i className="imap-swatch imap-solid"></i> measured &ndash; from the inventory
        </span>
        <span>
          <i className="imap-swatch imap-dashed"></i> assumed &ndash; not seen, inferred
        </span>
        <span>
          <i className="imap-swatch imap-dotted"></i> hosted &ndash; a guest on that machine
        </span>
        <span>
          <i className="fas fa-exclamation-triangle imap-warn"></i> needs confirming
        </span>
        <span>
          <i className="imap-swatch imap-unreg"></i> seen, but not in the inventory
        </span>
        <span>
          <i className="imap-swatch imap-office-box"></i> office &ndash; the devices of one office (All offices)
        </span>
      </div>

      <div className="imap-stage">
        <div className="imap-canvas-wrap" ref={wrap} onKeyDown={onMapKey}>
          <div id="imapCanvas" ref={canvas} tabIndex={0} aria-label="Network map. Use + and - to zoom, 0 to fit."></div>
          <button
            type="button"
            className="btn btn-default btn-xs imap-reset"
            id="imapReset"
            title="Put every node back where the map would draw it"
            onClick={() => {
              forgetPositions(positionsScope)
              /* Cheia folosita o zi de asezarea pe randuri (26.10.2.7); nu se mai citeste. */
              if (office === '') forgetPositions('all.offices')
              void graph.refetch()
            }}
          >
            <i className="fas fa-undo"></i> Reset positions
          </button>
          <div className="imap-note" id="imapNote" style={{ display: searchLc ? undefined : 'none' }}>
            {searchLc ? `Find: ${search}` : ''}
          </div>
          <div className="imap-empty" id="imapEmpty" style={{ display: empty ? undefined : 'none' }}>
            <i className="fas fa-project-diagram"></i>
            <p>Nothing to draw for this selection.</p>
            <p className="imap-empty-note">Devices appear here once they have reported an internal IP address.</p>
          </div>
          <ZoomBar cy={cyNow} onFit={() => cyRef.current && fitMap(cyRef.current)} />
          <div className="imap-loading" id="imapLoading" style={{ display: graph.isFetching ? undefined : 'none' }}>
            Drawing...
          </div>
        </div>
        <aside className="imap-side" id="imapDetail" style={{ display: panelOpen ? undefined : 'none' }}>
          {editing ? (
          <div className="imap-quick" id="imapQuick">
            <div className="imap-quick-head" id="imapQuickHead">
              {promoting ? 'Add to the inventory' : adding ? `Add this ${GEAR[editing.kind ?? ''] ?? 'device'}` : 'Edit'}
            </div>
            <div className="alert alert-danger" id="imapQuickError" style={{ display: quickError ? undefined : 'none' }}>
              {quickError}
            </div>
            <label htmlFor="imapQuickName">Name</label>
            <input type="text" className="form-control input-sm" id="imapQuickName" defaultValue={editing.name ?? ''} key={'n' + (editing.deviceId ?? editing.foundId ?? editing.segmentKey ?? 'new')} />
            <label htmlFor="imapQuickType">Type</label>
            <select className="form-control input-sm" id="imapQuickType" defaultValue={editing.typeId ? String(editing.typeId) : undefined} key={'t' + (editing.typeId ?? '')}>
              {types.map((t) => (
                <option key={t.id} value={String(t.id)}>
                  {t.name}
                </option>
              ))}
            </select>
            <label htmlFor="imapQuickBrand">Brand</label>
            <div className="input-group input-group-sm">
              <select className="form-control input-sm" id="imapQuickBrand" defaultValue={editing.brandId ? String(editing.brandId) : ''} key={'b' + (editing.brandId ?? '')}>
                <option value="">Not set</option>
                {brands.map((b) => (
                  <option key={b.id} value={String(b.id)}>
                    {b.name}
                  </option>
                ))}
              </select>
              <span className="input-group-btn">
                <button className="btn btn-default btn-sm" type="button" id="imapQuickBrandNew" title="Add a brand without leaving this form">
                  <i className="fas fa-plus"></i>
                </button>
              </span>
            </div>
            <label htmlFor="imapQuickOffice">Office</label>
            <select className="form-control input-sm" id="imapQuickOffice" defaultValue={editing.officeId ? String(editing.officeId) : office} key={'o' + (editing.officeId ?? '')}>
              {offices.map((o) => (
                <option key={o.ID} value={String(o.ID)}>
                  {o.Nume}
                </option>
              ))}
            </select>
            {adding || promoting ? (
              <dl className="imap-facts" id="imapQuickKnown" style={{ display: editing.ip || editing.mac || editing.vendor ? undefined : 'none' }}>
                {editing.ip ? (
                  <>
                    <dt>IP</dt>
                    <dd>{editing.ip}</dd>
                  </>
                ) : null}
                {editing.mac ? (
                  <>
                    <dt>MAC</dt>
                    <dd>{editing.mac}</dd>
                  </>
                ) : null}
                {editing.vendor ? (
                  <>
                    <dt>Made by</dt>
                    <dd>{editing.vendor}</dd>
                  </>
                ) : null}
              </dl>
            ) : null}
            {adding && !promoting ? (
              <div id="imapQuickAdd">
                <label htmlFor="imapQuickIp">IP address</label>
                <input
                  type="text"
                  className="form-control input-sm"
                  id="imapQuickIp"
                  placeholder="10.0.0.2"
                  autoComplete="off"
                  defaultValue={editing.ip ?? ''}
                  key={'ip' + (editing.ip ?? editing.segmentKey ?? 'new')}
                />
                <p className="imap-muted" id="imapQuickNoIp">
                  {editing.ip
                    ? 'Address taken from the map. SNMP is optional - only if you manage it that way.'
                    : 'Without an address it goes to the inventory but cannot be placed on the map.'}
                </p>
                <div className="checkbox">
                  <SettingsCheck id="imapQuickManaged" checked={managed} onChange={setManaged} label={<>Managed &ndash; SNMP</>} />
                </div>
                <div id="imapQuickSnmp" style={{ display: managed ? undefined : 'none' }}>
                  <div className="imap-quick-pair">
                    <div>
                      <label htmlFor="imapQuickVersion">Version</label>
                      <select className="form-control input-sm" id="imapQuickVersion" value={snmpVersion} onChange={(e) => setSnmpVersion(e.target.value)}>
                        <option value="2c">v2c</option>
                        <option value="1">v1</option>
                        <option value="3">v3</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="imapQuickPort">Port</label>
                      <input type="number" className="form-control input-sm" id="imapQuickPort" defaultValue={161} />
                    </div>
                  </div>
                  <div id="imapQuickCommunityBox" style={{ display: snmpVersion === '3' ? 'none' : undefined }}>
                    <label htmlFor="imapQuickCommunity">Community</label>
                    <input type="password" className="form-control input-sm" id="imapQuickCommunity" autoComplete="new-password" />
                  </div>
                  <div id="imapQuickV3" style={{ display: snmpVersion === '3' ? undefined : 'none' }}>
                    <label htmlFor="imapQuickSnmpUser">SNMP user</label>
                    <input type="text" className="form-control input-sm" id="imapQuickSnmpUser" autoComplete="off" />
                    <label htmlFor="imapQuickSnmpPass">SNMP password</label>
                    <input type="password" className="form-control input-sm" id="imapQuickSnmpPass" autoComplete="new-password" />
                  </div>
                  <span className="imap-muted">Kept in the vault, encrypted.</span>
                  <div className="checkbox">
                    <SettingsCheck id="imapQuickHasLogin" checked={hasLogin} onChange={setHasLogin} label="Login credentials" />
                  </div>
                  <div id="imapQuickLogin" style={{ display: hasLogin ? undefined : 'none' }}>
                    <label htmlFor="imapQuickLoginUser">User</label>
                    <input type="text" className="form-control input-sm" id="imapQuickLoginUser" autoComplete="off" />
                    <label htmlFor="imapQuickLoginPass">Password</label>
                    <input type="password" className="form-control input-sm" id="imapQuickLoginPass" autoComplete="new-password" />
                  </div>
                </div>
              </div>
            ) : null}
            {editing.deviceId ? (
              <div>
                <label htmlFor="imapQuickUplink">Uplink</label>
                <select className="form-control input-sm" id="imapQuickUplink" defaultValue={editing.uplinkTo ? String(editing.uplinkTo) : '0'}>
                  <option value="0">Not linked</option>
                  {allNodes
                    .filter((n) => n.deviceId && numberOf(n.deviceId) !== editing.deviceId && (n.kind === 'switch' || n.kind === 'gateway' || n.kind === 'ap'))
                    .map((n) => (
                      <option key={text(n.deviceId)} value={text(n.deviceId)}>
                        {text(n.label ?? n.deviceId)}
                      </option>
                    ))}
                </select>
              </div>
            ) : null}
            <div className="imap-quick-drop-row">
              <button
                type="button"
                className="btn btn-xs btn-link imap-quick-drop"
                id="imapQuickIgnore"
                style={{ display: adding && !promoting && editing.segmentKey ? undefined : 'none' }}
                disabled={saving}
                onClick={() => void dropAssumed()}
              >
                There is no switch here
              </button>
            </div>
            <div className="imap-quick-foot">
              <button type="button" className="btn btn-xs btn-default" id="imapQuickCancel" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button type="button" className="btn btn-xs btn-primary" id="imapQuickSave" disabled={saving} onClick={() => void saveQuick()}>
                {adding ? 'Add' : 'Save'}
              </button>
            </div>
          </div>
          ) : selected ? (
            detailBody(selected)
          ) : null}
        </aside>
      </div>
    </Content>
  )
}
